import { ThemeProvider } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import React, { useCallback, useEffect, useState } from 'react'

import axios from 'axios'
import defaultTheme from './theme'

import { FormProvider } from './context/form'

const { REACT_APP_SHOP_URL } = process.env

export default function withRoot<P>(
  Component: React.ComponentType<P>,
  apiKey: string,
  config: any
) {
  const WithRoot = (props: any) => {
    const [isLoading, setIsLoading] = useState(true)
    const [theme, setTheme] = useState<any>({})
    const getDefaultTheme = (themeId: string) => {
      switch (themeId) {
        case 'blue':
          return {
            theme: 'royal',
            primaryColor: '#002554',
            secondaryColor: '#fdf8f3',
          }
        case 'brown':
          return {
            theme: 'hazelnut',
            primaryColor: '#b1a296',
            secondaryColor: '#dff1ff',
          }
        case 'red':
          return {
            theme: 'ruby',
            primaryColor: '#ff385c',
            secondaryColor: '#464646',
          }
        case 'green':
          return {
            theme: 'jade',
            primaryColor: '#41b3a3',
            secondaryColor: '#fff0e6',
          }
        default:
          throw new Error('Invalid theme id.')
      }
    }
    const getClient = useCallback(async () => {
      try {
        setIsLoading(true)
        if (apiKey) {
          const { data } = await axios.get(
            `${REACT_APP_SHOP_URL}/api/v1/shop/client/get?apiKey=${apiKey}`
          )
          const {
            email,
            logo,
            name,
            config: productConfig,
            attributes: { theme: themeId, version },
            emailCustomization,
            flowCustomization,
            appointmentCustomization,
            customization,
            stripe,
            products,
            links,
            files,
            workHours,
            googleAnalyticsTrackingId,
            configOverride,
          } = data
          let customFlowData
          if (flowCustomization) {
            customFlowData = await axios.get(
              `${REACT_APP_SHOP_URL}/api/v1/shop/flow?id=${flowCustomization}`
            )
          }

          const themeProps = {
            apiKey: apiKey,
            themeId: getDefaultTheme(themeId)?.theme,
            email,
            logo,
            name,
            version,
            config: productConfig,
            appConfig: {
              ...config,
              ...configOverride,
            },
            customization: {
              fontFamily: customization?.fontFamily,
              primaryColor:
                customization?.primaryColor ||
                getDefaultTheme(themeId)?.primaryColor,
              secondaryColor:
                customization?.secondaryColor ||
                getDefaultTheme(themeId)?.secondaryColor,
              companyName: customization?.companyName || 'Meet Parker',
              botAvatar: customization?.botAvatar,
              expertPhoneNumber: customization?.expertPhoneNumber || '',
              botName: customization?.botName || 'Parker',
              parkerSlideBG: customization?.parkerSlideBG || null,
              parkerSlideTextColor:
                customization?.parkerSlideTextColor || '#000',
            },
            emailCustomization,
            appointmentCustomization,
            flowCustomization,
            customFlowData: customFlowData?.data,
            stripe,
            products,
            files,
            links,
            workHours,
            googleAnalyticsTrackingId,
          }

          setTheme(themeProps)
        } else {
          // navigate("/404");
        }
      } catch (err) {
        console.log(err)
        console.error(err)
      } finally {
        setIsLoading(false)
      }
      /*eslint-disable react-hooks/exhaustive-deps*/
    }, [])
    useEffect(() => {
      getClient()
    }, [getClient])
    const fontFamily = theme?.customization?.fontFamily
      ? `${theme?.customization?.fontFamily} !important`
      : `Poppins, sans-serif !important`
    return isLoading ? null : (
      <ThemeProvider
        theme={{
          ...defaultTheme,
          palette: {
            ...defaultTheme?.palette,
            primary: {
              main: theme?.customization?.primaryColor,
            },
            secondary: {
              main: theme?.customization?.secondaryColor,
            },
          },
          custom: theme,
          typography: {
            ...defaultTheme?.typography,
            fontFamily,
            body1: {
              ...defaultTheme?.typography?.body1,
              fontFamily,
            },
            body2: {
              ...defaultTheme?.typography?.body2,
              fontFamily,
            },
            button: {
              ...defaultTheme?.typography?.button,
              fontFamily,
            },
            caption: {
              ...defaultTheme?.typography?.caption,
              fontFamily,
            },
            h1: {
              ...defaultTheme?.typography?.h1,
              fontFamily,
            },
            h2: {
              ...defaultTheme?.typography?.h2,
              fontFamily,
            },
            h3: {
              ...defaultTheme?.typography?.h3,
              fontFamily,
            },
            h4: {
              ...defaultTheme?.typography?.h4,
              fontFamily,
            },
            h5: {
              ...defaultTheme?.typography?.h5,
              fontFamily,
            },
            h6: {
              ...defaultTheme?.typography?.h6,
              fontFamily,
            },
            subtitle1: {
              ...defaultTheme?.typography?.subtitle1,
              fontFamily,
            },
            subtitle2: {
              ...defaultTheme?.typography?.subtitle2,
              fontFamily,
            },
          },
        }}
      >
        {/* {!isLoading && (
          <Helmet>
            <title>{`${theme?.customization?.companyName} Best Buy Tables`}</title>
            <meta
              name="theme-color"
              content={theme?.customization?.primaryColor}
            />
            <meta
              name="title"
              content={`${theme?.customization?.companyName} Best Buy Tables`}
            />
            <meta
              name="description"
              content="Find the Best Mortgage UK Rates in the market"
            />
            <meta property="og:type" content="website" />

            <meta
              property="og:title"
              content={`${theme?.customization?.companyName} Best Buy Tables`}
            />

            <meta
              property="og:description"
              content="Find the Best Mortgage UK Rates in the market"
            />

            <meta
              property="og:url"
              content="https://www.twenty7tec.com/communicate"
            />

            <meta
              property="og:site_name"
              content={`${theme?.customization?.companyName} Best Buy Tables`}
            />

            <meta property="og:image" content={theme?.logo} />
          </Helmet>
        )} */}
        <CssBaseline />
        <FormProvider>
          <Component {...props} apiKey={apiKey} />
        </FormProvider>
      </ThemeProvider>
    )
  }

  return WithRoot
}
